import React, { useEffect, useRef, useState } from "react";

import { type ItemHit, searchItems } from "../services/tagApi";

/**
 * Debounced catalog search + result list for choosing the product an EPC maps
 * to. Used by both Match Tags (pick once) and Manage Tags' edit modal. Calls
 * `onSelect` with the chosen item; the parent owns the "selected" state.
 */
interface ProductPickerProps {
  token: string;
  onSelect: (item: ItemHit) => void;
  selectedId?: number | null;
  placeholder?: string;
  autoFocus?: boolean;
}

export const ProductPicker: React.FC<ProductPickerProps> = ({
  token,
  onSelect,
  selectedId,
  placeholder = "Search SKU or name…",
  autoFocus,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ItemHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    const id = ++reqIdRef.current;
    const t = setTimeout(() => {
      setLoading(true);
      searchItems(q, token)
        .then((items) => {
          if (id === reqIdRef.current) {
            setResults(items);
            setError(null);
          }
        })
        .catch((e) => {
          if (id === reqIdRef.current) {
            setError(e instanceof Error ? e.message : String(e));
          }
        })
        .finally(() => {
          if (id === reqIdRef.current) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [query, token]);

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
      />
      {error && <div className="mt-1 text-xs text-error-700">{error}</div>}
      {loading && <div className="mt-1 text-xs text-secondary-400">searching…</div>}
      {results.length > 0 && (
        <ul className="mt-2 max-h-56 overflow-auto border border-secondary-200 rounded divide-y divide-secondary-100">
          {results.map((it) => (
            <li key={it.item_id}>
              <button
                type="button"
                onClick={() => onSelect(it)}
                className={
                  "w-full text-left px-3 py-2 hover:bg-secondary-50 " +
                  (selectedId === it.item_id ? "bg-primary-50" : "")
                }
              >
                <div className="font-mono text-sm text-secondary-900">
                  {it.sku ?? `#${it.item_id}`}
                </div>
                {it.name && <div className="text-xs text-secondary-500">{it.name}</div>}
                {(it.manufacturer || it.category) && (
                  <div className="text-[11px] text-secondary-400">
                    {[it.manufacturer, it.category].filter(Boolean).join(" · ")}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && query.trim().length >= 2 && results.length === 0 && !error && (
        <div className="mt-1 text-xs text-secondary-400">no matching items</div>
      )}
    </div>
  );
};

export default ProductPicker;
